import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Spinner from '../ui/Spinner';
import DatePicker from '../ui/DatePicker';
import { pushToast } from '../ui/toastBus';
import { getAssignOptions, approveAndAssign } from '../../api/assignments';
import { StaffRadioList, nowLocalInput, toIso } from './AssignDeadlineFields';

// "Approve and assign": approves the request, then hands the step that follows it to exactly one
// staff member (those holding that step's process permission) with a completion deadline.
export default function ApproveAssignModal({ approvalId, open, onClose, onDone }) {
  const [assignee, setAssignee] = useState(null);
  const [deadline, setDeadline] = useState('');

  const options = useQuery({
    queryKey: ['assign-options', approvalId],
    queryFn: () => getAssignOptions(approvalId),
    enabled: open,
  });

  const mutation = useMutation({
    mutationFn: () => approveAndAssign(approvalId, { assigned_to: assignee, deadline: toIso(deadline) }),
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Request approved and step assigned.' });
      onDone();
      onClose();
    },
    onError: (err) => pushToast({ type: 'error', message: err.response?.data?.message || 'Could not approve and assign.' }),
  });

  const nextStep = options.data?.next_step;
  const valid = assignee !== null && deadline && new Date(deadline) > new Date();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Approve and assign"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending} disabled={!valid || !nextStep}>
            Approve and assign
          </Button>
        </>
      }
    >
      {options.isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : !nextStep ? (
        <p className="rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-600">
          There is no following step to assign for this request — use plain Approve.
        </p>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            After approval, <span className="font-medium text-gray-900">{nextStep.title}</span> is assigned to the staff member you
            select. Only they can update that step.
          </p>
          <div>
            <p className="mb-1 text-sm font-medium text-gray-700">Assign to</p>
            <StaffRadioList
              staff={options.data.staff}
              value={assignee}
              onChange={setAssignee}
              emptyText={`No active staff hold the permission to process ${nextStep.title}.`}
            />
          </div>
          <DatePicker
            label="Deadline"
            type="datetime-local"
            min={nowLocalInput()}
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
          />
        </div>
      )}
    </Modal>
  );
}
